import {
    ClientWithContacts,
    findClientContactByRole,
    ClientContactLike,
    contactDisplayName,
} from './clientContacts';
import { ccEmailsOf, requesterEmailsOf, type RequesterSource } from './changeRequester';

export type QuoteRecipientOption = {
    email: string;
    label: string;
    role?: string;
    isPrimary?: boolean;
};

/**
 * Prefill To: facturation → administratif → client.email
 */
export function defaultQuoteRecipientEmail(client: ClientWithContacts | null | undefined): string {
    const facturation = findClientContactByRole(client, 'facturation');
    if (facturation?.email)
        return String(facturation.email).trim();

    const administratif = findClientContactByRole(client, 'administratif');
    if (administratif?.email)
        return String(administratif.email).trim();

    return String(client?.email || '').trim();
}

export type QuoteSendRecipientDefaults = {
    to: string[];
    cc: string[];
    signalUsers: string[];
    signalGroups: string[];
};

/**
 * Prefill send recipients from tracked card replyRecipients when present,
 * otherwise fall back to commercial client contacts.
 */
export function defaultQuoteSendRecipients(
    client: ClientWithContacts | null | undefined,
    trackedChange?: RequesterSource | null,
): QuoteSendRecipientDefaults {
    const toFromCard = trackedChange ? requesterEmailsOf(trackedChange) : [];
    const ccFromCard = trackedChange ? ccEmailsOf(trackedChange) : [];
    type SignalBucket = { signalUsers?: string[]; signalGroups?: string[] };
    const toBucket = trackedChange?.replyRecipients?.to as SignalBucket | null | undefined;
    const ccBucket = trackedChange?.replyRecipients?.cc as SignalBucket | null | undefined;
    const signalUsers = [
        ...(toBucket?.signalUsers || []),
        ...(ccBucket?.signalUsers || []),
    ].map((id) => String(id)).filter(Boolean);
    const signalGroups = [
        ...(toBucket?.signalGroups || []),
        ...(ccBucket?.signalGroups || []),
    ].map((id) => String(id)).filter(Boolean);

    const unique = (emails: string[]) => {
        const seen = new Set<string>();
        const out: string[] = [];
        emails.forEach((email) => {
            const key = String(email || '').trim().toLowerCase();
            if (!key || seen.has(key))
                return;
            seen.add(key);
            out.push(String(email).trim());
        });
        return out;
    };

    if (toFromCard.length) {
        return {
            to: unique(toFromCard),
            cc: unique(ccFromCard),
            signalUsers: [...new Set(signalUsers)],
            signalGroups: [...new Set(signalGroups)],
        };
    }

    const fallback = defaultQuoteRecipientEmail(client);
    return {
        to: fallback ? [fallback] : [],
        cc: [],
        signalUsers: [...new Set(signalUsers)],
        signalGroups: [...new Set(signalGroups)],
    };
}

export function quoteRecipientOptions(client: ClientWithContacts | null | undefined): QuoteRecipientOption[] {
    const options: QuoteRecipientOption[] = [];
    const seen = new Set<string>();

    const push = (email: string, label: string, role?: string, isPrimary?: boolean) => {
        const normalized = email.trim().toLowerCase();
        if (!normalized || seen.has(normalized))
            return;
        seen.add(normalized);
        options.push({ email: email.trim(), label, role, isPrimary });
    };

    if (client?.email) {
        const name = [client.firstname, client.lastname].filter(Boolean).join(' ').trim();
        push(client.email, name || client.email, undefined, true);
    }

    (client?.contacts || []).forEach((contact: ClientContactLike) => {
        const email = String(contact.email || '').trim();
        if (!email)
            return;
        const name = contactDisplayName(contact) || email;
        const role = (contact.roles || [])[0];
        push(email, name, role ? String(role) : undefined);
    });

    return options;
}
